import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { DebugModeService } from "../../../services/debug-mode.service";
import { hitlIssueCodes } from "../../../model/interrupt.types";
import type { InterruptItem } from "../../../model/interrupt.types";

/**
 * Engine-stamped ask context (blackboard HITL contract §2): the issue codes explaining
 * why the ask exists plus how many times this task has already been asked. Rendered
 * inside the host card's header badge slot — the cards project it via `notifBadge`
 * while pending.
 *
 * DEBUG-ONLY (2026-10-20 ruling): raw engine metadata reads as internals, so it renders
 * only under `DebugModeService` (Alt+0). User-facing feedback for an ask belongs in the
 * engine's `display_text`, not in metadata chips.
 */
@Component({
  selector: "app-hitl-ask-context",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: "./hitl-ask-context.component.html",
  styleUrl: "./hitl-ask-context.component.scss",
})
export class HitlAskContextComponent {
  entry = input.required<InterruptItem>();

  readonly #debugMode = inject(DebugModeService);

  protected readonly isDebugMode = this.#debugMode.isDebugMode;

  /** Issue codes stamped by the engine, rendered verbatim as chips. */
  protected readonly issueCodes = computed(() => hitlIssueCodes(this.entry()));

  /** How many times this task has already been asked, 0 when unstamped. */
  protected readonly askedCount = computed(() => {
    const raw = (this.entry().metadata ?? {}).plan_surfaces;
    return typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
  });
}